<template>
	<div>
		<Breadcrumbs main="System Management" title="Log Center" />
		<div class="container-fluid">
			<div class="row">
				<div class="col-12">
					<div class="card">
						<div class="card-header d-flex justify-content-between align-items-center">
							<div>
								<h5>Application Event Logs</h5>
								<span>Search and filter system events by keyword and date range.</span>
							</div>
							<button class="btn btn-outline-primary" type="button" @click="fetchLogs" :disabled="loading">
								Refresh
							</button>
						</div>

						<div class="card-body">
							<div v-if="errorMessage" class="alert alert-danger" role="alert">{{ errorMessage }}</div>

							<div class="row g-3 mb-3 align-items-end">
								<div class="col-md-4">
									<label class="form-label">Search Event</label>
									<input
										v-model.trim="filters.query"
										type="text"
										class="form-control"
										placeholder="username, action, resource, details..."
										@keyup.enter="onSearch"
									/>
								</div>
								<div class="col-md-3">
									<label class="form-label">Start Date</label>
									<input v-model="filters.startDate" type="date" class="form-control" />
								</div>
								<div class="col-md-3">
									<label class="form-label">End Date</label>
									<input v-model="filters.endDate" type="date" class="form-control" />
								</div>
								<div class="col-md-2 d-flex gap-2">
									<button class="btn btn-primary w-100" type="button" @click="onSearch" :disabled="loading">
										Search
									</button>
									<button class="btn btn-outline-secondary w-100" type="button" @click="clearFilters" :disabled="loading">
										Clear
									</button>
								</div>
							</div>

							<div class="table-responsive">
								<table class="table table-striped table-bordered">
									<thead>
										<tr>
											<th>Timestamp</th>
											<th>Username</th>
											<th>Action</th>
											<th>Resource</th>
											<th>Result</th>
											<th>Details</th>
										</tr>
									</thead>
									<tbody>
										<tr v-if="loading">
											<td colspan="6" class="text-center">Loading logs...</td>
										</tr>
										<tr v-else-if="!logs.length">
											<td colspan="6" class="text-center">No logs found for current filters.</td>
										</tr>
										<tr v-for="log in logs" :key="log.id">
											<td>{{ formatDate(log.timestamp) }}</td>
											<td>{{ log.username || '-' }}</td>
											<td>{{ log.action || '-' }}</td>
											<td>{{ log.resource || '-' }}</td>
											<td>
												<span :class="log.result === 'Success' ? 'badge badge-success' : 'badge badge-danger'">
													{{ log.result || '-' }}
												</span>
											</td>
											<td class="log-details">{{ log.details || '-' }}</td>
										</tr>
									</tbody>
								</table>
							</div>

							<div v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-2">
								<small class="text-muted">
									Page {{ pagination.pageNumber }} of {{ totalPages }} ({{ pagination.totalItems }} records)
								</small>
								<div class="d-flex gap-2">
									<button
										class="btn btn-sm btn-outline-secondary"
										:disabled="pagination.pageNumber <= 1"
										@click="goToPage(pagination.pageNumber - 1)"
									>
										&laquo; Prev
									</button>
									<button
										class="btn btn-sm btn-outline-secondary"
										:disabled="pagination.pageNumber >= totalPages"
										@click="goToPage(pagination.pageNumber + 1)"
									>
										Next &raquo;
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { API_BASE } from '../../config/api';

export default {
	name: 'LogIndex',
	data() {
		return {
			baseUrl: `${API_BASE}/api/AuditLog`,
			loading: false,
			logs: [],
			errorMessage: '',
			filters: {
				query: '',
				startDate: '',
				endDate: '',
			},
			pagination: {
				pageNumber: 1,
				pageSize: 20,
				totalItems: 0,
			},
		};
	},
	computed: {
		permissions() {
			return this.$store?.state?.auth?.permissions || [];
		},
		canRead() {
			return this.permissions.includes('systems:read');
		},
		totalPages() {
			if (this.pagination.totalItems === 0) return 1;
			return Math.ceil(this.pagination.totalItems / this.pagination.pageSize);
		},
	},
	mounted() {
		if (!this.canRead) {
			this.errorMessage = 'You do not have permission to view logs.';
			return;
		}

		this.fetchLogs();
	},
	methods: {
		getHeaders() {
			return {
				Authorization: `Bearer ${localStorage.getItem('token')}`,
				'Content-Type': 'application/json',
			};
		},
		buildSearchUrl() {
			const params = new URLSearchParams();
			params.set('pageNumber', this.pagination.pageNumber.toString());
			params.set('pageSize', this.pagination.pageSize.toString());

			if (this.filters.query) {
				params.set('query', this.filters.query);
			}

			if (this.filters.startDate) {
				const startUtc = new Date(`${this.filters.startDate}T00:00:00`);
				params.set('startDate', startUtc.toISOString());
			}

			if (this.filters.endDate) {
				const endUtc = new Date(`${this.filters.endDate}T23:59:59.999`);
				params.set('endDate', endUtc.toISOString());
			}

			return `${this.baseUrl}/search?${params.toString()}`;
		},
		async fetchLogs() {
			this.loading = true;
			this.errorMessage = '';

			try {
				const response = await fetch(this.buildSearchUrl(), {
					method: 'GET',
					headers: this.getHeaders(),
				});

				if (!response.ok) {
					throw new Error(`Failed to load logs (${response.status})`);
				}

				const payload = await response.json();
				this.logs = payload.data?.data || [];
				this.pagination.totalItems = payload.total || 0;
			} catch (error) {
				this.errorMessage = error?.message || 'Unable to load logs.';
			} finally {
				this.loading = false;
			}
		},
		onSearch() {
			this.pagination.pageNumber = 1;
			this.fetchLogs();
		},
		clearFilters() {
			this.filters.query = '';
			this.filters.startDate = '';
			this.filters.endDate = '';
			this.pagination.pageNumber = 1;
			this.fetchLogs();
		},
		goToPage(pageNumber) {
			if (pageNumber < 1 || pageNumber > this.totalPages) {
				return;
			}

			this.pagination.pageNumber = pageNumber;
			this.fetchLogs();
		},
		formatDate(value) {
			if (!value) return '-';
			const date = new Date(value);
			if (Number.isNaN(date.getTime())) return '-';
			return date.toLocaleString('en-GB');
		},
	},
};
</script>

<style scoped>
.log-details {
	max-width: 360px;
	white-space: normal;
	word-break: break-word;
}
</style>
